import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, MenuItem,
  LinearProgress, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Card, CardContent, CardActions, Stack,
  useTheme, useMediaQuery, TablePagination,
} from '@mui/material';
import { Add, Search, CheckCircle, Cancel, Visibility } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expenseService, projectService, farmOperationService } from '@smartgrow/shared';
import type { ExpenseRequest, ExpenseStatus } from '@smartgrow/shared';

const STATUS_COLORS: Record<ExpenseStatus, 'default' | 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  PAID: 'default',
};

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;

export const ExpensesPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId') ?? '';
  const projectName = searchParams.get('projectName') ?? '';
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [rejectDialog, setRejectDialog] = useState<ExpenseRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(urlProjectId);
  const [selectedOperationId, setSelectedOperationId] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState('');

  const projectId = selectedProjectId || urlProjectId;

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdmin = userData?.role === 'ADMIN' || userData?.role === 'OWNER';

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data: projectsData } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: operationsData } = useQuery({
    queryKey: ['operations-for-filter', projectId],
    queryFn: () => farmOperationService.getByProject(projectId),
    enabled: !!projectId,
  });

  const { data: tasksData } = useQuery({
    queryKey: ['tasks-for-filter', selectedOperationId],
    queryFn: () => farmOperationService.getTasksByOperation(selectedOperationId),
    enabled: !!selectedOperationId,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['expenses', statusFilter, isAdmin, page, projectId, selectedOperationId, selectedTaskId, debouncedSearch],
    queryFn: () => {
      if (!isAdmin) return expenseService.getMyExpenses(page, 10);
      return expenseService.search({
        page, size: 10,
        search: debouncedSearch || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        projectId: projectId || undefined,
        farmOperationId: selectedOperationId || undefined,
        operationTaskId: selectedTaskId || undefined,
      });
    },
  });

  const clearProjectFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('projectId');
    next.delete('projectName');
    setSearchParams(next);
    setSelectedProjectId('');
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPage(0);
  };

  const handleProjectChange = (value: string) => {
    setSelectedProjectId(value);
    setSelectedOperationId('');
    setSelectedTaskId('');
    setPage(0);
    if (!value && urlProjectId) clearProjectFilter();
  };

  const handleOperationChange = (value: string) => {
    setSelectedOperationId(value);
    setSelectedTaskId('');
    setPage(0);
  };

  const filtered: ExpenseRequest[] = data?.content ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['expenses'] });

  const approveMutation = useMutation({
    mutationFn: (id: string) => expenseService.approve(id),
    onSuccess: () => { invalidate(); setError(''); },
    onError: () => setError('Approval failed.'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      expenseService.reject(id, reason),
    onSuccess: () => { invalidate(); setRejectDialog(null); setRejectReason(''); setError(''); },
    onError: () => setError('Rejection failed.'),
  });

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">Expense Requests</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/expenses/new')} size={isMobile ? 'small' : 'medium'}>
          New Expense
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {urlProjectId && !selectedProjectId && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`Project: ${projectName || urlProjectId}`}
            onDelete={clearProjectFilter}
            color="primary"
            variant="outlined"
          />
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField select size="small" label="Project" value={projectId}
            onChange={(e) => handleProjectChange(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Projects</MenuItem>
            {(projectsData?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Operation" value={selectedOperationId}
            onChange={(e) => handleOperationChange(e.target.value)} disabled={!projectId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Operations</MenuItem>
            {(operationsData?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Task" value={selectedTaskId}
            onChange={(e) => { setSelectedTaskId(e.target.value); setPage(0); }} disabled={!selectedOperationId} sx={{ minWidth: 180 }}>
            <MenuItem value="">All Tasks</MenuItem>
            {(tasksData ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ flex: 1, minWidth: 180 }}
          />
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} sx={{ minWidth: 130 }}>
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="APPROVED">Approved</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {isMobile ? (
        <Stack spacing={2}>
          {!isLoading && filtered.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No expense requests found</Typography>
          )}
          {filtered.map((e: ExpenseRequest) => (
            <Card key={e.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/expenses/${e.id}`)}>
              <CardContent sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">{e.requestNumber}</Typography>
                  <Chip label={e.status} color={STATUS_COLORS[e.status]} size="small" />
                </Box>
                <Typography variant="body2" fontWeight="medium" gutterBottom>{e.description}</Typography>
                {e.rejectionReason && (
                  <Typography variant="caption" color="error.main" display="block">Reason: {e.rejectionReason}</Typography>
                )}
                <Typography variant="body2" color="text.secondary">{e.farmOperationName ?? e.projectName ?? '—'}</Typography>
                {e.operationTaskTitle && (
                  <Typography variant="caption" color="text.secondary" display="block">Task: {e.operationTaskTitle}</Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2"><strong>{fmt(e.amount)}</strong></Typography>
                  <Typography variant="caption" color="text.secondary">{e.requestDate}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">{e.categoryName} · {e.requestedByName}</Typography>
              </CardContent>
              {isAdmin && (
                <CardActions sx={{ pt: 0 }} onClick={(ev) => ev.stopPropagation()}>
                  <IconButton size="small" onClick={() => navigate(`/dashboard/expenses/${e.id}`)}><Visibility fontSize="small" /></IconButton>
                  {e.status === 'PENDING' && (
                    <>
                      <IconButton size="small" color="success"
                        onClick={() => approveMutation.mutate(e.id)}
                        disabled={approveMutation.isPending}>
                        <CheckCircle fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error"
                        onClick={() => { setRejectDialog(e); setRejectReason(''); }}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </CardActions>
              )}
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>Ref</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Linked To</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Requested By</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                {isAdmin && <TableCell><strong>Actions</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 10 : 9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No expense requests found
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((e: ExpenseRequest, i: number) => (
                <TableRow key={e.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/expenses/${e.id}`)}>
                  <TableCell>{page * 10 + i + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">{e.requestNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{e.description}</Typography>
                    {e.rejectionReason && (
                      <Typography variant="caption" color="error.main">
                        Reason: {e.rejectionReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {e.farmOperationName ?? e.projectName ?? '—'}
                    </Typography>
                    {e.operationTaskTitle && (
                      <Typography variant="caption" color="text.secondary">
                        Task: {e.operationTaskTitle}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{e.categoryName}</TableCell>
                  <TableCell><strong>{fmt(e.amount)}</strong></TableCell>
                  <TableCell>{e.requestDate}</TableCell>
                  <TableCell>{e.requestedByName}</TableCell>
                  <TableCell>
                    <Chip label={e.status} color={STATUS_COLORS[e.status]} size="small" />
                  </TableCell>
                  {isAdmin && (
                    <TableCell onClick={(ev) => ev.stopPropagation()}>
                      <IconButton size="small" onClick={() => navigate(`/dashboard/expenses/${e.id}`)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                      {e.status === 'PENDING' && (
                        <>
                          <IconButton size="small" color="success"
                            onClick={() => approveMutation.mutate(e.id)}
                            disabled={approveMutation.isPending}>
                            <CheckCircle fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error"
                            onClick={() => { setRejectDialog(e); setRejectReason(''); }}>
                            <Cancel fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}

      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Expense</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {rejectDialog?.description} — {rejectDialog ? fmt(rejectDialog.amount) : ''}
          </Typography>
          <TextField
            fullWidth required multiline rows={3} label="Rejection Reason"
            value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={!rejectReason || rejectMutation.isPending}
            onClick={() => rejectMutation.mutate({ id: rejectDialog!.id, reason: rejectReason })}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
